import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Eye, FileText, ChevronLeft, ChevronRight } from 'lucide-react'
import apiService from '../services/api'

interface VendorQuote {
  id: string
  projectName?: string
  status: string
  createdAt: string
  buyer?: {
    companyName?: string
    firstName?: string
    lastName?: string
  }
  listing?: {
    title?: string
  }
}

interface VendorQuoteDashboardProps {
  hideHeader?: boolean
}

export default function VendorQuoteDashboard({ hideHeader = false }: VendorQuoteDashboardProps) {
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState<VendorQuote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const quotesPerPage = 10

  useEffect(() => {
    fetchQuotes()
  }, [])

  const fetchQuotes = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiService.get('/api/enterprise/quotes/vendor')
      console.log('Vendor quotes response:', response)
      // API returns {success: true, data: {quotes: [...quotes...]}}
      setQuotes((response as any).data?.quotes || [])
      // Reset to first page when new data is loaded
      setCurrentPage(1)
    } catch (error: any) {
      console.error('Failed to fetch vendor quotes:', error)
      setError(error.response?.data?.message || 'Failed to load quotes')
    } finally {
      setLoading(false)
    }
  }

  // Pagination calculations
  const totalPages = Math.ceil(quotes.length / quotesPerPage)
  const startIndex = (currentPage - 1) * quotesPerPage
  const endIndex = startIndex + quotesPerPage
  const currentQuotes = quotes.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    // Scroll to top of quotes section when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT': return 'bg-blue-100 text-blue-800'
      case 'RECEIVED': return 'bg-yellow-100 text-yellow-800'
      case 'RESPONDED': return 'bg-purple-100 text-purple-800'
      case 'ACCEPTED': return 'bg-green-100 text-green-800'
      case 'REJECTED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Quote Requests Received</h2>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading quote requests...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Quote Requests Received</h2>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchQuotes}
            className="btn btn-primary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header - conditionally rendered */}
      {!hideHeader && (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Quote Requests Received</h2>
            <p className="text-gray-600">Manage quote requests from enterprise buyers</p>
          </div>
          <button
            onClick={fetchQuotes}
            className="btn btn-outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      )}

      {/* Quote List */}
      {quotes.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 min-h-[300px] flex flex-col justify-center">
          <FileText className="w-20 h-20 text-gray-400 dark:text-gray-500 mx-auto mb-6" />
          <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-3">No quote requests received</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">Quote requests from enterprise buyers will appear here when they request quotes for your products or services</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 overflow-hidden">
          {/* Table Header */}
          <div className="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              <div className="sm:col-span-2">Buyer & Quote Details</div>
              <div className="hidden sm:block">Status</div>
              <div className="hidden sm:block">Date Received</div>
              <div className="text-right">Actions</div>
            </div>
          </div>

          {/* Quote Rows */}
          <div className="divide-y divide-gray-200">
            {currentQuotes.map((quote) => (
              <div key={quote.id} className="px-4 sm:px-6 py-4 hover:bg-gray-50">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 items-center">
                  {/* Buyer & Quote Details - Combined on mobile */}
                  <div className="sm:col-span-2 space-y-1">
                    <div className="font-medium text-gray-900 text-sm sm:text-base">
                      {quote.buyer?.companyName || `${quote.buyer?.firstName} ${quote.buyer?.lastName}` || 'Enterprise Buyer'}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      {quote.projectName || quote.listing?.title || `Quote #${quote.id.slice(-8)}`}
                    </div>
                    {/* Status shown below on mobile */}
                    <div className="sm:hidden mt-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(quote.status)}`}>
                        {quote.status}
                      </span>
                    </div>
                    {/* Date shown below on mobile */}
                    <div className="sm:hidden text-xs text-gray-500 mt-1">
                      Received: {formatDate(quote.createdAt)}
                    </div>
                  </div>

                  {/* Status - Hidden on mobile */}
                  <div className="hidden sm:block">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(quote.status)}`}>
                      {quote.status}
                    </span>
                  </div>

                  {/* Date Received - Hidden on mobile */}
                  <div className="hidden sm:block text-gray-600 text-sm">
                    {formatDate(quote.createdAt)}
                  </div>

                  {/* View Button */}
                  <div className="text-right">
                    <button
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                      className="inline-flex items-center px-2 sm:px-3 py-1.5 border border-gray-300 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 touch-manipulation"
                    >
                      <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      <span className="hidden sm:inline">View</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">
                  Showing {startIndex + 1}-{Math.min(endIndex, quotes.length)} of {quotes.length} quote requests
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and pages around current
                    return page === 1 ||
                           page === totalPages ||
                           (page >= currentPage - 1 && page <= currentPage + 1)
                  })
                  .map((page, index, array) => {
                    // Add ellipsis where needed
                    const prevPage = array[index - 1]
                    const showEllipsis = prevPage && page - prevPage > 1

                    return (
                      <div key={page} className="flex items-center">
                        {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 rounded-md text-sm font-medium ${
                            currentPage === page
                              ? 'text-white bg-primary-600'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    )
                  })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md text-sm font-medium text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
} 